/**
 * @file StockLevelsPanel.tsx
 * @description Вкладка «Остатки»: фильтры, состояния экрана, счётчик строк
 * (phase19.24 этап 1, шаг 2; размещение из журнала — шаг 4; групповая печать
 * этикеток — phase19.22, W17-9c).
 *
 * Панель отвечает только за «что показать» и за правило «какую строку можно
 * отметить»: в «Без места» — партию без адреса, в «Без этикеток» /
 * «Этикетки устарели» — партию с остатком, и одну партию только один раз
 * (строка остатка — склад × партия, этикетка — на партию). Работа со строками
 * — в `StockLevelsResults`, загрузка — в `useStockLevels`.
 *
 * Выбор строк (`useStockSelection`) держит панель: при пустом отборе список
 * не отрисован, и одноразовый `?select=all` некому было бы снять с адреса.
 */

import { Package, PackageSearch, Search, Tags } from "lucide-react";
import { useCallback, useMemo } from "react";

import { StockLevelsFilters } from "./StockLevelsFilters";
import { StockLevelsResults } from "./StockLevelsResults";
import { firstRowPerLot, isPrintable } from "./stock-label-print";
import { shouldShowWarehouse } from "./stock-level-format";
import { isPlaceable } from "./stock-row-actions";
import type { StockFilters } from "./use-stock-filters";
import { STOCK_WINDOW, useStockLevels } from "./use-stock-levels";
import { useStockSelection } from "./use-stock-selection";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/EmptyState";
import { LIST_SURFACE_CLASS, ListSkeleton } from "../../ui/ListStates";
import { NoticeStrip } from "../../ui/NoticeStrip";
import type { StockLevelDto, WarehouseDto } from "../../../lib/api-wms";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";

interface Props {
  filters: StockFilters;
  onChange: (patch: Partial<StockFilters>) => void;
  onReset: () => void;
  dirty: boolean;
  warehouses: WarehouseDto[];
}

/** Пустой отбор по этикеткам — это хорошая новость, а не «ничего не нашлось». */
const LABELS_EMPTY: Record<"none" | "stale", string> = {
  none: "Все партии на складе с этикетками",
  stale: "Устаревших этикеток нет",
};

export function StockLevelsPanel({ filters, onChange, onReset, dirty, warehouses }: Props) {
  const { visible, loading, error, truncated, reload } = useStockLevels(filters);
  const labelsMode = filters.labels !== "";
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const firstOfLot = useMemo(() => firstRowPerLot(visible), [visible]);

  /** Можно ли отметить строку и подпись, если нельзя (см. шапку файла). */
  const selectState = useCallback(
    (level: StockLevelDto): { can: boolean; hint: string } => {
      if (filters.unplaced && isPlaceable(level)) return { can: true, hint: "" };
      if (!labelsMode) return { can: false, hint: level.lot ? "уже размещена" : "без партии" };
      if (!isPrintable(level)) return { can: false, hint: level.lot ? "нет остатка" : "без партии" };
      if (!firstOfLot.has(level.id)) return { can: false, hint: "партия уже в выборе" };
      return { can: true, hint: "" };
    },
    [filters.unplaced, labelsMode, firstOfLot],
  );
  const canSelect = useCallback((level: StockLevelDto) => selectState(level).can, [selectState]);

  const selection = useStockSelection({
    rows: visible,
    enabled: filters.unplaced || labelsMode,
    ready: !loading,
    canSelect,
  });

  const emptyAction = (
    <Button variant="outline" onClick={onReset}>
      Сбросить отбор
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <StockLevelsFilters
        filters={filters}
        onChange={onChange}
        onReset={onReset}
        dirty={dirty}
        warehouses={warehouses}
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && truncated ? (
        <NoticeStrip tone="info">
          Показаны первые {STOCK_WINDOW} позиций отбора — сузьте склад или
          уточните поиск, чтобы увидеть остальные.
        </NoticeStrip>
      ) : null}

      {loading ? (
        // Заготовки строк — на поверхности списка, не на фоне кабинета (SC69).
        <ListSkeleton rows={5} compact={compact} />
      ) : visible.length === 0 ? (
        filters.labels !== "" ? (
          <EmptyState className={LIST_SURFACE_CLASS} icon={Tags} title={LABELS_EMPTY[filters.labels]} action={emptyAction} />
        ) : dirty ? (
          <EmptyState
            className={LIST_SURFACE_CLASS}
            icon={Search}
            variant="filtered"
            title="По этому отбору остатков нет"
            description="Проверьте название, артикул или номер партии либо сбросьте фильтры."
          />
        ) : (
          <EmptyState
            className={LIST_SURFACE_CLASS}
            icon={PackageSearch}
            title="Остатков нет"
            description="Товар встаёт на склад после проведения приёмки."
          />
        )
      ) : (
        <>
          {/* Счётчик лежит на фоне кабинета — основным цветом, число полужирным (SC69). */}
          <p className="type-body flex items-center gap-1.5 text-on-surface">
            <Package className="size-4 text-on-surface-variant" aria-hidden />
            Позиций: <span className="font-semibold tabular-nums">{visible.length}</span>
          </p>
          <StockLevelsResults
            showWarehouse={shouldShowWarehouse(warehouses.length, filters.warehouseId, visible)}
            items={visible}
            selectable={filters.unplaced || labelsMode}
            selection={selection}
            selectState={labelsMode ? selectState : undefined}
            placing={filters.unplaced}
            labelsMode={labelsMode}
            printWarehouseId={filters.warehouseId}
            onChanged={reload}
          />
        </>
      )}
    </div>
  );
}
